import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/api-context';
import { useJob } from '../api/useJob';
import type { Job, RepertoireLine } from '../api/types';
import { LINE_NAME_MAX_LENGTH } from '../lib/line-name';
import { saveImportedVariation } from '../state/import-variation';
import { buildNodeRoute } from '../state/route';

/** Server PGN parsing followed by named repertoire paths via the existing APIs. */
export function ImportPanel({ repertoireId }: { repertoireId: string }): JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const [pgn, setPgn] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<Job | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState('');
  const [saved, setSaved] = useState<RepertoireLine[]>([]);
  const [pendingGames, setPendingGames] = useState<string[]>([]);
  const submittedName = useRef('Imported variation');
  const finishingJob = useRef<string | null>(null);
  const { job, ui, error: jobError } = useJob(accepted?.jobId ?? null, accepted);
  const importing = submitting || saving || (accepted !== null && !ui.isTerminal);

  async function saveGames(ids: string[]): Promise<void> {
    setSaving(true);
    setError(null);
    let remaining = [...ids];
    try {
      for (const [index, gameId] of ids.entries()) {
        const line = await saveImportedVariation(api, repertoireId, gameId,
          ids.length === 1 ? submittedName.current : `${submittedName.current} ${index + 1}`, setProgress);
        setSaved((previous) => [...previous, line]);
        remaining = remaining.slice(1);
        setPendingGames(remaining);
      }
      setProgress('Import complete. Your variations are saved.');
    } catch (e) {
      setPendingGames(remaining);
      setError(e instanceof Error ? e.message : 'Could not save the imported variation. Try again.');
    } finally {
      setSaving(false);
      for (const key of ['repertoire', 'repertoires', 'lines', 'node', 'explorer', 'comparison-games']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    }
  }

  // Claim each completed job once, including React StrictMode effect replay.
  useEffect(() => {
    if (!ui.succeeded || !job || finishingJob.current === job.jobId) return;
    finishingJob.current = job.jobId;
    const ids = job.result?.game_ids;
    if (!Array.isArray(ids) || !ids.length || !ids.every((id) => typeof id === 'string')) {
      setError('The parser returned no game moves. Check the PGN and try again.');
      return;
    }
    setPendingGames(ids);
    void saveGames(ids);
    // saveGames consumes the immutable submission name and current API scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.succeeded, job]);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!pgn.trim() || importing) return;
    setSubmitting(true);
    setError(null);
    setSaved([]);
    setPendingGames([]);
    setAccepted(null);
    setProgress('Validating PGN…');
    submittedName.current = name.trim() || 'Imported variation';
    try {
      setAccepted(await api.createImport({ source: 'PgnImport', pgn: pgn.trim(), repertoireIds: [repertoireId] }, crypto.randomUUID()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return <section className="panel" aria-labelledby="import-title">
    <h2 id="import-title">Import variation from PGN</h2>
    <p className="hint">Paste a move sequence or PGN. Each game’s main line becomes a saved variation in this repertoire. Existing move settings are preserved.</p>
    <form onSubmit={onSubmit} aria-label="Import variation">
      <div className="field">
        <label htmlFor="import-name">Imported variation name</label>
        <input id="import-name" value={name} onChange={(e) => setName(e.target.value)} disabled={importing} maxLength={LINE_NAME_MAX_LENGTH} placeholder="Imported variation" />
      </div>
      <div className="field">
        <label htmlFor="pgn-input">Paste PGN</label>
        <textarea id="pgn-input" value={pgn} onChange={(e) => setPgn(e.target.value)} disabled={importing} placeholder="1. d4 d5 2. Bf4 e6 3. e3 Nf6" rows={4} />
      </div>
      <button type="submit" disabled={!pgn.trim() || importing}>{importing ? 'Importing…' : 'Import variation'}</button>
    </form>
    {error && <p role="alert" className="error">{error}</p>}
    {accepted && ui.failed && <p role="alert">{job?.error?.detail || 'Could not parse the PGN. Check the move sequence and try again.'}</p>}
    {accepted && ui.status === 'cancelled' && <p role="alert">Import was cancelled. Your pasted PGN is still here.</p>}
    {accepted && jobError && <p role="alert">Could not refresh import status. Retrying…</p>}
    {importing && <p role="status">{saving || submitting ? progress : ui.label}</p>}
    {!importing && pendingGames.length > 0 && error && <button onClick={() => void saveGames(pendingGames)}>Retry saving imported variation</button>}
    {saved.length > 0 && <div role="status">
      <p>Saved {saved.length} imported variation{saved.length === 1 ? '' : 's'}.</p>
      {saved.map((line) => <p key={line.lineId}><Link to={buildNodeRoute({ repertoireId, zobristHash: line.nodes[0].zobristHash, positionId: line.nodes[0].positionId, path: { lineId: line.lineId, ply: 0 } })}>Open {line.name || 'imported variation'}</Link></p>)}
    </div>}
  </section>;
}
